import React, { useState, useEffect } from "react";
import "./survey-tab.m.css";

export const SurveyTab = ({ surveysData: initialSurveysData, userEmail }) => {
  const [surveysData, setSurveysData] = useState(initialSurveysData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visiblePreviousSurveys, setVisiblePreviousSurveys] = useState(2);

  const shouldShowSurveyToUser = (survey) => {
    if (!userEmail || !survey['survey-show']) return false;

    // Check if userEmail exists in the survey-show array of objects
    const showList = Array.isArray(survey['survey-show'])
      ? survey['survey-show']
      : [];

    const isUserAllowed = showList.some(user => user.name === userEmail);
    return isUserAllowed;
  };

  useEffect(() => {
    if (initialSurveysData) {
      setSurveysData(initialSurveysData);
    } else if (!surveysData && !loading) {
      loadSurveysData();
    }
  }, [initialSurveysData]);

  const loadSurveysData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/v1/entities?type=surveys&limit=50');
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();
      const { entities = [] } = responseData;

      // Transform and categorize surveys
      const currentDate = new Date();
      const latestSurveys = [];
      const previousSurveys = [];
      entities.forEach(survey => {
        if (!survey['deleted-at'] && shouldShowSurveyToUser(survey)) {
          // Parse the last-day date
          const lastDayParts = survey['last-day'].split('/');
          const lastDay = new Date(parseInt(lastDayParts[2]), parseInt(lastDayParts[1]) - 1, parseInt(lastDayParts[0]) + 1);

          if (lastDay >= currentDate) {
            latestSurveys.push(survey);
          } else {
            previousSurveys.push(survey);
          }
        }
      });

      // Sort by created date (most recent first)
      latestSurveys.sort((a, b) => new Date(b['created-at']) - new Date(a['created-at']));
      previousSurveys.sort((a, b) => new Date(b['created-at']) - new Date(a['created-at']));

      setSurveysData({
        latestSurveys,
        previousSurveys,
        total: entities.length
      });
    } catch (err) {
      console.error('Error loading surveys:', err);
      setError(err.message || 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setVisiblePreviousSurveys(prev => prev + 5);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                     'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      const day = parseInt(parts[0]);
      const month = months[parseInt(parts[1]) - 1];
      const year = parts[2];
      return `${day} ${month}, ${year}`;
    }
    return dateString;
  };

  const hasUserFilledSurvey = (survey) => {
    if (!userEmail || !survey['survey-filled-list']) return false;

    // Check if userEmail exists in the survey-filled-list
    const filledList = Array.isArray(survey['survey-filled-list'])
      ? survey['survey-filled-list']
      : [];

    return filledList.some(user => user.name === userEmail);
  };

  const renderSurveyCard = (survey, isLatest = false) => {
    const cardClass = isLatest ? 'survey-card' : 'previous-survey-card';
    const detailsClass = isLatest ? 'survey-details' : 'previous-survey-details';
    const titleClass = isLatest ? 'survey-title' : 'previous-survey-title';
    const buttonClass = isLatest ? 'survey-button' : 'previous-survey-button';

    return (
      <div key={survey.id} styleName={`${isLatest ? 'latest-survey-unit' : ''}`}>
        <div styleName={cardClass}>
          <div styleName={detailsClass}>
            <div styleName="survey-title-brief">
              <div styleName="survey-title-container">
                <div styleName={isLatest ? "survey-title-heading" : ""}>
                  <h3 styleName={titleClass}>
                    {survey.name}
                  </h3>
                </div>
              </div>
              <div styleName="survey-description-container">
                <p styleName="survey-description">
                  {survey['survey-sub-headline']}
                </p>
              </div>
            </div>
            {isLatest ? (
              <div styleName="survey-metadata">
                <div styleName="time-date-section">
                  {survey['time-required'] && <div styleName="time-section">
                    <div styleName="metadata-label-container">
                      <p styleName="metadata-label">Time required</p>
                    </div>
                    <div styleName="metadata-value-container">
                      <p styleName="metadata-value">{survey['time-required']} minutes</p>
                    </div>
                  </div>}
                  <div styleName="date-section">
                    <div styleName="metadata-label-container">
                      <p styleName="metadata-label">Complete by</p>
                    </div>
                    <div styleName="metadata-value-container">
                      <p styleName="metadata-value">{formatDate(survey['last-day'])}</p>
                    </div>
                  </div>
                </div>
                {hasUserFilledSurvey(survey) ? (
                  <div styleName="survey-button-disabled">
                    <p styleName="survey-button-text">
                      Survey completed
                    </p>
                  </div>
                ) : (
                  <a
                    href={survey['survey-link']}
                    target="_blank"
                    rel="noopener noreferrer"
                    styleName={buttonClass}
                  >
                    <p styleName="survey-button-text">
                      Take the survey
                    </p>
                  </a>
                )}
              </div>
            ) : null}
          </div>
          {!isLatest && (
            <div styleName="previous-survey-metadata">
              <div styleName="date-section">
                <div styleName="metadata-label-container">
                  <p styleName="metadata-label">Complete by</p>
                </div>
                <div styleName="metadata-value-container">
                  <p styleName="metadata-value">{formatDate(survey['last-day'])}</p>
                </div>
              </div>
              {hasUserFilledSurvey(survey) && (
                <a
                  href={survey['story-url']}
                  target="_blank"
                  rel="noopener noreferrer"
                  styleName={buttonClass}
                >
                  <p styleName="survey-button-text">
                    Read report
                  </p>
                </a>
              )}
            </div>
          )}
          {isLatest && (
            <div styleName="survey-notes">
              <div styleName="note-container">
                <p styleName="note-text">
                  {survey['survey-point']}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div styleName="survey-tab">
        <div styleName="loading-container">
          <p>Loading surveys...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div styleName="survey-tab">
        <div styleName="error-container">
          <p styleName="error-text">{error}</p>
          <button styleName="retry-button" onClick={loadSurveysData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { latestSurveys, previousSurveys } = surveysData || {};

  return (
    <div styleName="survey-tab">
      {/* Latest Survey Section */}
      <div styleName="section-title">
        <h2 styleName="section-heading">Latest survey</h2>
      </div>

      <div styleName="survey-section">
        {latestSurveys && latestSurveys.length > 0 ? (
          latestSurveys.slice(0, 2).map(survey => renderSurveyCard(survey, true))
        ) : (
          <div styleName="empty-state">
            <p styleName="empty-state-text">No latest surveys available</p>
          </div>
        )}
      </div>

      {/* Survey footnote - displayed once below latest surveys */}
      {latestSurveys && latestSurveys.length > 0 && (
        <div styleName="survey-footnote">
          <p styleName="footnote-text">
            * Always use the email you registered with IMA to fill out the survey
          </p>
        </div>
      )}

      {/* Previous Surveys Section */}
      <div styleName="section-title">
        <h2 styleName="section-heading">Previous surveys</h2>
      </div>

      <div styleName="survey-section">
        {previousSurveys && previousSurveys.length > 0 ? (
          <>
            {previousSurveys.slice(0, visiblePreviousSurveys).map(survey =>
              renderSurveyCard(survey, false)
            )}
            {previousSurveys.length > visiblePreviousSurveys && (
              <div styleName="load-more-container">
                <button styleName="load-more-link" onClick={handleLoadMore}>
                  <div styleName="load-more-text">
                    <p styleName="load-more-label">Load more</p>
                  </div>
                </button>
              </div>
            )}
          </>
        ) : (
          <div styleName="empty-state">
            <p styleName="empty-state-text">No previous surveys available</p>
          </div>
        )}
      </div>
    </div>
  );
};

SurveyTab.propTypes = {
  surveysData: require('prop-types').object,
  userEmail: require('prop-types').string
};